import { AnimatePresence, motion } from 'framer-motion';
import {
  AudioConfig,
  AudioOutputStream,
  ResultReason,
  SpeechConfig,
  SpeechRecognizer,
  SpeechSynthesisOutputFormat,
  SpeechSynthesizer,
} from 'microsoft-cognitiveservices-speech-sdk';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAudioRecorder } from 'react-audio-voice-recorder';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRecoilState, useRecoilValue } from 'recoil';
import { v4 as uuidv4 } from 'uuid';
import SettingsCard from '../components/SettingsCard';
import { agentState, isVoiceSettingsOpen, voicePersona } from '../recoil';
import { sendMessageToAuroraStreamingVoice } from '../services/api';

import Dots from '../assets/icons/VoiceConversation/Dots.png';
import Mic from '../assets/icons/VoiceConversation/mic.png';
import Pause from '../assets/icons/VoiceConversation/pause.png';
import Stop from '../assets/icons/VoiceConversation/stop.png';

import useWindowDimensions from '../services/utils';

import Lottie from 'react-lottie-player';
import { chatHistoryAtom } from '../recoil/chatHistory/atom';

import * as pectyIdle from '../assets/lotties/pecty/pectyIdle.json';
import * as pectyJoy from '../assets/lotties/pecty/pectyJoy.json';
import * as pectyListening from '../assets/lotties/pecty/pectyListening.json';
import * as pectyWave from '../assets/lotties/pecty/pectyWave.json';
import * as pectyResponding from '../assets/lotties/pecty/pectyResponding.json';
import VoiceItinerary from '../components/VoiceItinerary';
import {
  itineraryState,
  sendExternalState,
  sessionIdState,
  topicsState,
} from '../recoil';
import Chat from '../assets/icons/VoiceConversation/chat.png';
import MicAndVisualizer from '../components/MicAndVisualizer';
import SettingsGear from '../assets/icons/SettingsGear';

import { isMobile } from 'react-device-detect';

const SPEECH_KEY = process.env.REACT_APP_SPEECH_KEY;
const SPEECH_REGION = process.env.REACT_APP_SPEECH_REGION;
const QUESTION_TIME_SECONDS = 15;
const FINALIZE_DELAY_MS = 1000;
const IDLE_TIMEOUT_MS = 10 * 60 * 1000;
// Smaller chunks = faster time-to-first-audio; too small splits mid-phrase (e.g. "like me to | tell you about").
const MAX_TTS_CHUNK_CHARS = 160;

interface CustomPlayer {
  play: () => void;
  pause: () => void;
  stop: () => void;
  onEnd: (callback: () => void) => void;
}

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const VoiceConversation: React.FC = () => {
  const [currentAgent, setCurrentAgent] = useRecoilState(agentState);
  const [detectEmotion] = useState<string>('');

  const recorder = useAudioRecorder();
  const [showHello, setShowHello] = useState(true);
  const [showImSpark, setShowImSpark] = useState(false);
  const [showYouCanSay, setShowYouCanSay] = useState(false);
  const [convoStarted, setConvoStarted] = useState(false);
  const [showMic, setShowMic] = useState(false);

  const [aiIdle, setAIIdle] = useState(true);
  const [aiListening, setAiListening] = useState(false);
  const [aiThinking, setAiThinking] = useState(false);
  const [aiTalking, setAiTalking] = useState(false);
  const [, setAiError] = useState(false);

  const aiPausedRef = useRef<boolean>(false);

  const [isConvoReady, setIsConvoReady] = useState(false);
  const [chatHistory, setChatHistory] = useRecoilState(chatHistoryAtom);

  const [isRecognizerActive, setIsRecognizerActive] = useState<boolean>(false);
  const [questionTimeLeft, setQuestionTimeLeft] = useState<number>(0);

  const synthesizerRef = useRef<SpeechSynthesizer | undefined>();
  const recognizerRef = useRef<SpeechRecognizer>();
  const countdownIntervalRef = useRef<number | null>(null);
  const finalizeTimeoutRef = useRef<number | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  const playerRef = useRef<CustomPlayer>();
  const audioRef = useRef<HTMLAudioElement>();
  const audioQueueRef = useRef<Blob[]>([]);
  const isPlayingRef = useRef(false);
  const spokenResponsesRef = useRef<Set<string>>(new Set());
  const streamBufferRef = useRef<string>('');
  const responseBufferRef = useRef<string>('');
  const [testCounter, setTestCounter] = useState<number>(0);
  const interruptRef = useRef<boolean>(false);

  const isClosedRef = useRef<boolean>(false);

  const navigate = useNavigate();

  const [settingsCardOpen, setIsSettingsCardOpen] =
    useRecoilState(isVoiceSettingsOpen);

  const voicePersonaOption = useRecoilValue(voicePersona);
  const handleToggleSettingsCard = () => {
    setIsSettingsCardOpen((prevState) => !prevState);
  };

  const [initialLoad] = useState<boolean>(true);

  const [, setTopics] = useRecoilState(topicsState);
  const [isItineraryOpen, setIsItineraryOpen] = useState(false);
  const [itinerary, setItinerary] = useRecoilState(itineraryState);
  const [sessionId, setSessionId] = useRecoilState(sessionIdState);
  const sendExternal = useRecoilValue(sendExternalState);
  const WEBSOCKET_URL =
    process.env.REACT_APP_WEBSOCKET_URL ||
    'wss://3wropptbh6.execute-api.us-east-1.amazonaws.com/Prod';

  const speechConfig: SpeechConfig = SpeechConfig.fromSubscription(
    SPEECH_KEY || '',
    SPEECH_REGION || ''
  );

  speechConfig.speechRecognitionLanguage = 'en-US';
  speechConfig.speechSynthesisVoiceName = voicePersonaOption.AzureCode;
  speechConfig.speechSynthesisOutputFormat =
    SpeechSynthesisOutputFormat.Audio16Khz32KBitRateMonoMp3;

  const audioConfigRec: AudioConfig = AudioConfig.fromDefaultMicrophoneInput(); //listening

  const voiceToChat = () => {
    const currentUrl = new URL(window.location.href);
    const currentPath = currentUrl.pathname;
    console.log('CURRENT URL: ', currentUrl);

    const newPath = currentPath.replace('voice', 'chat');
    const newUrl = `${newPath}${currentUrl.search}`;
    console.log('NEW URL: ', newUrl);

    navigate(newUrl);
    // Trigger a reload after navigation
    setTimeout(() => {
      window.location.reload();
    }, 100);
  };

  const markActivity = () => {
    lastActivityRef.current = Date.now();
  };

  const clearFinalizeTimeout = () => {
    if (finalizeTimeoutRef.current !== null) {
      window.clearTimeout(finalizeTimeoutRef.current);
      finalizeTimeoutRef.current = null;
    }
  };

  const clearCountdown = () => {
    if (countdownIntervalRef.current !== null) {
      window.clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  };

  const formatCountdown = (value: number) => {
    const clamped = Math.max(0, value);
    const minutes = Math.floor(clamped / 60)
      .toString()
      .padStart(2, '0');
    const seconds = (clamped % 60).toString().padStart(2, '0');
    return `${minutes}:${seconds}`;
  };

  let onEndCallback = () => {
    console.log('ON END CALLBACK -- handleManualPlayback');
    if (synthesizerRef.current) {
      synthesizerRef.current?.close();
      synthesizerRef.current = undefined;
    }
    clearFinalizeTimeout();
    clearCountdown();
    resetConversation();
  };

  function onClickInterrupt(
    event: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) {
    console.log('Element that triggered the onClickInterrupt:', event.target);
    console.log('ON CLICK INTERRUPT ---- INTERUPTING TALKING + RESET');
    console.log('CLOSED STATE', isClosedRef.current);
    interruptRef.current = true;
    markActivity();
    stopSpeechRecognition();
    if (synthesizerRef.current) {
      synthesizerRef.current?.close();
      synthesizerRef.current = undefined;
    }
    audioQueueRef.current = [];
    isPlayingRef.current = false;
    clearFinalizeTimeout();
    clearCountdown();
    if (playerRef.current && audioRef.current) {
      playerRef.current.pause();
      playerRef.current.stop();
    } else if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.muted = true;
    }
    resetConversation();
  }

  const initializeRecognizer = () => {
    console.log('Initializing Recognizer...');
    try {
      if (recognizerRef.current) {
        recognizerRef.current.stopContinuousRecognitionAsync(() => {
          recognizerRef.current?.close();
          recognizerRef.current = undefined;
        });
      }

      const recognizer = new SpeechRecognizer(speechConfig, audioConfigRec);
      recognizerRef.current = recognizer;

      recognizer.recognizing = (s, e) => {
        if (
          e.result.reason === ResultReason.RecognizingSpeech &&
          !aiPausedRef.current
        ) {
          console.log(`Recognizing: ${e.result.text}`);
        }
      };

      recognizer.recognized = async (s, e) => {
        if (
          e.result.reason === ResultReason.RecognizedSpeech &&
          !aiPausedRef.current
        ) {
          console.log(`User spoke: ${e.result.text}`);
          markActivity();
          if (finalizeTimeoutRef.current !== null) return;
          finalizeTimeoutRef.current = window.setTimeout(async () => {
            setAiListening(false);
            setAiThinking(true);
            setIsRecognizerActive(false);
            clearCountdown();

            recognizer.stopContinuousRecognitionAsync(() => {
              recognizer.close();
              recognizerRef.current = undefined;
            });

            await handleUserMessage(e.result.text);
            finalizeTimeoutRef.current = null;
          }, FINALIZE_DELAY_MS);
        }
      };

      recognizer.canceled = (s, e) => {
        console.error('Speech recognition canceled:', e.reason);
        setAiError(true);
        setAiListening(false);
        clearCountdown();
        setQuestionTimeLeft(0);
      };

      recognizer.sessionStopped = (s, e) => {
        console.log('Speech recognition session stopped.');
        setAiListening(false);
        clearCountdown();
        setQuestionTimeLeft(0);
      };
    } catch (error) {
      console.error('Error initializing recognizer:', error);
    }
  };

  const initializeSynthesizer = () => {
    try {
      const stream = AudioOutputStream.createPullStream();
      const audioConfigSpeak = AudioConfig.fromStreamOutput(stream);
      const newSynthesizer = new SpeechSynthesizer(
        speechConfig,
        audioConfigSpeak
      );
      synthesizerRef.current = newSynthesizer;

      synthesizerRef.current.SynthesisCanceled = (s, e) => {
        console.log('SYNTHESIZER CANCELED: ');
        resetConversation(); // Reset conversation on error
      };

      synthesizerRef.current.synthesisStarted = (s, e) => {
        console.log('SYNTHESIZER STARTED');
        setAiTalking(true);
        setAiThinking(false);
        if (recognizerRef.current) {
          recognizerRef.current.stopContinuousRecognitionAsync(() => {
            recognizerRef.current?.close();
            recognizerRef.current = undefined;
          });
        }
      };

      synthesizerRef.current.synthesisCompleted = (s, e) => {
        console.log('SYNTHESIZER COMPLETED');
      };
    } catch (err) {
      console.error('Error initializing synthesizer: ', err);
      resetConversation(); // Reset conversation on error
    }
  };

  const StartSpeechRecognition = () => {
    console.log('Starting Speech Recognition...');

    initializeRecognizer();
    // Reuse pre-warmed synthesizer if already created when convo became ready
    if (!synthesizerRef.current) {
      initializeSynthesizer();
    }

    if (isRecognizerActive) {
      console.log('Recognizer already active.');
      return;
    }

    setAIIdle(false);
    setAiListening(true);

    recognizerRef.current?.startContinuousRecognitionAsync(
      () => {
        console.log('Speech recognition started.');
        setIsRecognizerActive(true);
      },
      (err) => {
        console.error('Error starting recognition:', err);
        setAiError(true);
      }
    );
  };

  const stopSpeechRecognition = useCallback(() => {
    if (recognizerRef.current && isRecognizerActive) {
      console.log(`STOPPING SPEECH RECOGNITION`);
      try {
        recognizerRef.current.stopContinuousRecognitionAsync(() => {
          recognizerRef.current?.close();
          recognizerRef.current = undefined;
        });
      } catch (error) {
        console.error('Error stopping recognition:', error);
        recognizerRef.current?.close();
        recognizerRef.current = undefined;
      }

      setAiListening(false);
      if (recorder.isRecording) {
        recorder.stopRecording();
      }
      setIsRecognizerActive(false);
      return;
    }

    if (recognizerRef.current) {
      recognizerRef.current?.close();
      recognizerRef.current = undefined;
    }

    setAiListening(false);
    if (recorder.isRecording) {
      recorder.stopRecording();
    }
    setIsRecognizerActive(false);
  }, [isRecognizerActive, recorder]);

  async function MairSpeak(
    Synthesizer: SpeechSynthesizer | undefined,
    response?: string
  ) {
    if (!response || !Synthesizer) return;

    const normalizedResponse = response.trim();
    // Prevent duplicate speech synthesis
    if (spokenResponsesRef.current.has(normalizedResponse)) {
      console.warn('⚠️ Duplicate response detected, skipping:', response);
      return;
    }
    spokenResponsesRef.current.add(normalizedResponse);

    try {
      console.log('🔊 Synthesizing:', response);
      setAiTalking(true);

      Synthesizer.speakTextAsync(
        normalizedResponse,
        (result) => {
          if (result.reason === ResultReason.SynthesizingAudioCompleted) {
            console.log('✅ Audio synthesis complete:', response);
            const audioBlob = new Blob([result.audioData], {
              type: 'audio/mp3',
            });

            audioQueueRef.current.push(audioBlob);
            console.log(
              '📥 Audio queued. Queue length:',
              audioQueueRef.current.length
            );

            if (!isPlayingRef.current) {
              playNextInQueue();
            }
          } else {
            console.error('❌ TTS Error:', result.errorDetails);
          }
        },
        (err) => {
          console.error('❌ Speech synthesis error:', err);
        }
      );
    } catch (err) {
      console.error('❌ Error in MairSpeak:', err);
    }
  }

  async function playNextInQueue() {
    if (audioQueueRef.current.length === 0 || isPlayingRef.current) {
      setAiThinking(false);
      setAiListening(false);
      setAiTalking(false);
      setAIIdle(true);
      return;
    }

    isPlayingRef.current = true;
    const audioBlob = audioQueueRef.current.shift(); // Get next audio chunk
    if (!audioBlob) {
      isPlayingRef.current = false;
      return;
    }

    const audioUrl = URL.createObjectURL(audioBlob);
    const cleanupAudioUrl = () => URL.revokeObjectURL(audioUrl);
    if (audioRef.current) {
      console.log('▶️ Playing audio:', audioUrl);
      audioRef.current.src = audioUrl;
      audioRef.current.muted = false;

      try {
        await audioRef.current.play();
        console.log('🔊 Audio playing successfully');

        audioRef.current.onended = () => {
          cleanupAudioUrl();
          isPlayingRef.current = false;
          console.log('✅ Finished playing audio. Checking queue...');
          playNextInQueue();
        };
      } catch (error) {
        console.error('❌ Error playing synthesized audio:', error);
        cleanupAudioUrl();
        isPlayingRef.current = false;
        playNextInQueue();
      }
    } else {
      cleanupAudioUrl();
      isPlayingRef.current = false;
    }
  }

  const speakBufferedText = async (chunk: string, isFinal: boolean) => {
    if (chunk) {
      streamBufferRef.current += chunk;
    }

    const buffer = streamBufferRef.current;
    if (!buffer && !isFinal) return;

    // Flush on sentence end (.!?), clause boundaries (comma not in numbers, semicolon, em/en dash, newline), or when over max chunk size.
    // Comma is excluded when between digits (e.g. 1,000) so we don't split numbers.
    const clauseRegex =
      /[.!?;]\s*|(?<!\d),(?!\d)\s*|[\u2013\u2014]\s*|\n/g;
    const boundaryEndIndices: number[] = [];
    let match: RegExpExecArray | null;
    while ((match = clauseRegex.exec(buffer)) !== null) {
      boundaryEndIndices.push(match.index + match[0].length);
    }
    const lastBoundaryIndex =
      boundaryEndIndices.length > 0
        ? boundaryEndIndices[boundaryEndIndices.length - 1]
        : -1;

    let splitIndex = -1;
    if (buffer.length >= MAX_TTS_CHUNK_CHARS) {
      const lastBeforeMax = boundaryEndIndices.filter(
        (i) => i <= MAX_TTS_CHUNK_CHARS
      ).pop();
      if (lastBeforeMax !== undefined) {
        splitIndex = lastBeforeMax;
      } else {
        // No clause boundary before max length: split at last space so we don't break a word
        const searchUpTo = Math.min(MAX_TTS_CHUNK_CHARS, buffer.length);
        const lastSpace = buffer.slice(0, searchUpTo).lastIndexOf(' ');
        splitIndex = lastSpace > 0 ? lastSpace + 1 : MAX_TTS_CHUNK_CHARS;
      }
    } else if (lastBoundaryIndex > -1) {
      splitIndex = lastBoundaryIndex;
    }

    if (splitIndex > 0) {
      const toSpeak = buffer.slice(0, splitIndex).trim();
      streamBufferRef.current = buffer.slice(splitIndex);
      if (toSpeak) {
        await MairSpeak(synthesizerRef.current, toSpeak);
      }
      // Process remainder in same turn so we don't drop it when stream ends (isFinal)
      if (streamBufferRef.current.length > 0) {
        await speakBufferedText('', isFinal);
      }
    } else if (isFinal) {
      const remaining = buffer.trim();
      streamBufferRef.current = '';
      if (remaining) {
        await MairSpeak(synthesizerRef.current, remaining);
      }
    }
  };

  const handleUserMessage = async (userMessage: string) => {
    console.log("in handleUserMessage. Handle user's message:", userMessage);
    markActivity();
    setAiThinking(true);
    streamBufferRef.current = '';
    responseBufferRef.current = '';

    setChatHistory((prevHistory) => [
      ...prevHistory,
      { entity: 'human' as const, content: userMessage },
    ]);
    setConvoStarted(true);

    await sendMessageToAuroraStreamingVoice(
      userMessage,
      sessionId,
      currentAgent || 'spark',
      [],
      undefined,
      async (partialMessage, isFinal) => {
        console.log('Partial message:', partialMessage);
        if (!isFinal && partialMessage) {
          responseBufferRef.current += partialMessage;
        }
        setChatHistory((prevHistory) => {
          if (prevHistory.length === 0) return prevHistory;

          const lastMessage = prevHistory[prevHistory.length - 1];

          if (lastMessage.entity === 'ai') {
            return prevHistory.map((msg, index) =>
              index === prevHistory.length - 1
                ? { ...msg, content: responseBufferRef.current }
                : msg
            );
          }

          return [
            ...prevHistory,
            { entity: 'ai' as const, content: responseBufferRef.current },
          ];
        });
        await speakBufferedText(partialMessage, isFinal);
      }
    );
  };

  function onClickListening(
    event: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) {
    console.log('Element that triggered the onClickListening:', event.target);
    console.log('ON CLICK MAIN BUTTON ---- START RECOGNITION');

    setShowYouCanSay(false);
    markActivity();

    if (aiListening) {
      stopSpeechRecognition();
      clearCountdown();
      setQuestionTimeLeft(0);
      setAIIdle(true);
      setShowMic(true);
      return;
    }

    // Only when the user clicks mic button
    if (aiIdle) {
      if (audioRef.current) {
        // Reset the audio element
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current.muted = true;
        audioRef.current.preload = 'auto';
        audioRef.current.setAttribute('playsinline', 'true');
        audioRef.current.setAttribute('webkit-playsinline', 'true');
      } else {
        const audio = new Audio();
        audio.id = testCounter.toString();
        let num = testCounter + 1;
        setTestCounter(num);

        audio.muted = true;
        audio.preload = 'auto';
        audio.setAttribute('playsinline', 'true');
        audio.setAttribute('webkit-playsinline', 'true');
        audioRef.current = audio;
      }
    }

    let audio = audioRef.current;
    console.log('AUDIO REF: ', audio);
    console.log('AUDIO REF: ', audioRef.current);
    if (audio && aiIdle) {
      const customPlayer: CustomPlayer = {
        play: () => {
          if (audio) {
            audio.autoplay = true;
            audio.muted = false;
            let promise = audio.play();
            if (promise !== undefined) {
              promise
                .then((_) => {
                  console.log('AUTOPLAY STARTED');
                  if (audio) console.log('AUDIO ID - USING: ', audio.id);
                })
                .catch((error) => {
                  console.log('AUTOPLAY PREVENTED');
                  synthesizerRef.current?.close();
                  console.log('ERROR: ', error);
                });
            }
          }
        },
        pause: () => {
          if (audio) audio.pause();
        },
        stop: () => {
          if (audio) {
            audio.pause();
            audio.currentTime = 0;
            audio.muted = true;
            onEndCallback();
          }
        },
        onEnd: (callback) => {
          if (audio) {
            audio.muted = true;
            audio.onended = callback;
          }
        },
      };
      customPlayer.onEnd(onEndCallback);
      playerRef.current = customPlayer;
    }
    startCountdown();
    StartSpeechRecognition();
  }

  const resetConversation = useCallback(() => {
    console.log('RESETTING CONVERSATION');
    stopSpeechRecognition();
    clearFinalizeTimeout();
    clearCountdown();
    setAiListening(false);
    setAiThinking(false);
    setAiTalking(false);
    setAiError(false);
    setIsRecognizerActive(false);

    aiPausedRef.current = false;
    audioQueueRef.current = [];
    isPlayingRef.current = false;
    spokenResponsesRef.current.clear();
    streamBufferRef.current = '';
    responseBufferRef.current = '';

    setIsConvoReady(true);
    setShowMic(true);
    setAIIdle(true);
  }, [stopSpeechRecognition]);

  const startCountdown = useCallback(() => {
    setQuestionTimeLeft(QUESTION_TIME_SECONDS);
    clearCountdown();
    countdownIntervalRef.current = window.setInterval(() => {
      setQuestionTimeLeft((prev) => {
        if (prev <= 1) {
          clearCountdown();
          resetConversation();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [resetConversation]);

  // const handleManualPlayback = (
  //   audioData: ArrayBuffer,
  //   resetConversation: () => void,
  //   startSpeechRecognition: () => void
  // ) => {
  //   try {
  //     const audioBlob = new Blob([audioData], { type: 'audio/wav' });
  //     const audioUrl = URL.createObjectURL(audioBlob);
  //     if (audioRef.current) {
  //       audioRef.current.src = audioUrl;
  //       console.log('UPDATING AUDIO URL');
  //     }

  //     let audio = audioRef.current;
  //     let customPlayer = playerRef.current;
  //     if (audio && customPlayer) {
  //       audio.autoplay = true; //ADDED
  //       customPlayer.play();
  //       console.log('CUSTOM PLAYER PLAYING');
  //     }
  //   } catch (err) {
  //     console.error('Error in handleManualPlayback: ', err);
  //     resetConversation(); // Reset conversation on error
  //   }
  // };

  // const getActionText = () => {
  //   switch (true) {
  //     case aiListening:
  //       return <div>I'm Listening</div>;
  //     case aiThinking:
  //       return "I'm thinking";
  //     case aiTalking:
  //       return 'Tap to interrupt';
  //     case aiIdle:
  //       return <div>Tap to start speaking</div>;
  //     default:
  //       return '';
  //   }
  // };

  const getIcon = () => {
    const iconStyle = {
      filter:
        'brightness(0) saturate(100%) invert(16%) sepia(92%) saturate(4905%) hue-rotate(350deg) brightness(97%) contrast(104%)',
    };

    if (aiListening) {
      return (
        <img className="h-8 w-6" src={Pause} alt="mic" style={iconStyle} />
      );
    } else if (aiThinking || aiTalking) {
      return (
        <img className="h-8 w-6" src={Stop} alt="pause" style={iconStyle} />
      );
    } else if (aiIdle) {
      return <img className="h-8 w-6" src={Mic} alt="mic" style={iconStyle} />;
    }
    return null;
  };

  const getEmotionLottie = () => {
    if (detectEmotion === 'Joy') {
      return pectyJoy;
    } else {
      return pectyIdle;
    }
  };

  const query = useQuery();
  const agent = query.get('agent');

  useEffect(() => {
    let currAgent = '';

    if (agent) {
      switch (agent.toLowerCase()) {
        case 'shell':
          currAgent = 'shell';
          break;
        case 'spark':
          currAgent = 'spark';
          break;
        case 'laila-sxsw':
          currAgent = 'sxsw';
          break;
        case 'laila-oracle':
          currAgent = 'oracle';
          break;
        case 'laila-ny_genai':
          currAgent = 'ny_genai';
          break;
        case 'aslc_france':
          currAgent = 'aslc_france';
          break;
        case 'spark-houston':
          currAgent = 'houston';
          break;
        case 'spark-atlanta':
          currAgent = 'atlanta';
          break;
        case 'spark-new_york':
          currAgent = 'new_york';
          break;
        case 'spark-chicago':
          currAgent = 'chicago';
          break;
        case 'robin':
          currAgent = 'robin';
          break;
        case 'spark-hess':
          currAgent = 'hess';
          break;
        case 'felix':
          currAgent = 'felix';
          break;
        case 'mair':
          currAgent = 'mair';
          break;
        case 'mair-gpt':
          currAgent = 'mair-gpt';
          break;
        case 'spark-ihub':
          currAgent = 'ihub';
          break;
        default:
          // Handle the case where the agent parameter is not supported
          console.error('Invalid agent parameter:', agent);
          navigate('*');
          break;
      }
    } else {
      console.error('Please set an agent parameter');
      navigate('*');
    }

    if (currAgent !== '' && chatHistory.length === 2) {
      setCurrentAgent(currAgent);
      // Voice conversation uses no intro messages; chat conversation uses initializeChatHistory(currAgent).
      setChatHistory([]);
    }
  }, [agent, navigate, setCurrentAgent, setChatHistory, chatHistory.length]);

  const { width, height } = useWindowDimensions();

  const getAnimationSize = () => {
    if (width < 768 || height < 768) {
      // console.log('Mobile');
      return { width: 500, height: 500 };
    } else if (width < 1024 || height < 1024) {
      // console.log('Tablet');
      return { width: 700, height: 700 };
    } else {
      // console.log('Desktop');
      return { width: 800, height: 800 };
    }
  };

  useEffect(() => {
    if (aiIdle && !convoStarted) {
      const helloTimeout = window.setTimeout(() => {
        setShowHello(false);
        setShowImSpark(true);
      }, 3000);
      const sparkTimeout = window.setTimeout(() => {
        setShowImSpark(false);
        setIsConvoReady(true);
        setShowMic(true);
        if (convoStarted === false) {
          setShowYouCanSay(true);
        }
      }, 8000);

      return () => {
        window.clearTimeout(helloTimeout);
        window.clearTimeout(sparkTimeout);
      };
    }
  }, [aiIdle, convoStarted]);

  // Pre-warm TTS: create synthesizer when convo is ready so first user message
  // doesn't pay cold-start latency (connection + init).
  useEffect(() => {
    if (isConvoReady && !synthesizerRef.current) {
      initializeSynthesizer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run only when isConvoReady turns true; init is stable
  }, [isConvoReady]);

  useEffect(() => {
    let sessionId = uuidv4();
    setSessionId(sessionId);
    console.log(`New sessionId: ${sessionId}`);

    if (currentAgent === 'oracle' && sendExternal) {
      const websocket_id = 'DH-ORACLE';
      console.log(
        `websocket_id set to ${websocket_id}. Now sending to external site.`
      );
      return;
    }

    // const websocket_id = sessionId;

    // const url = `${WEBSOCKET_URL}/?websocket_id=${websocket_id}&initiator=FE`;
    // const ws = new ReconnectingWebSocket(url);

    // ws.onopen = () => {
    //   console.log('WebSocket connection opened');
    //   console.log(`WebSocket url: ${url}`);
    // };

    // ws.onmessage = (event) => {
    //   const data: Data = JSON.parse(event.data);
    //   console.log('Data', data);
    //   if (data.itinerary) {
    //     console.log('Itinerary data received', data.itinerary);
    //     setItinerary(data.itinerary);
    //     setIsItineraryOpen(true);
    //     setSessionId(data.session_id);
    //     setTopics(data.topics);
    //   }
    // };

    // ws.onclose = () => {
    //   console.log('WebSocket connection closed');
    // };

    // return () => {
    //   ws.close();
    // };
  }, [
    WEBSOCKET_URL,
    currentAgent,
    sendExternal,
    setItinerary,
    setSessionId,
    setTopics,
  ]);

  const handleCloseItinerary = () => {
    setIsItineraryOpen(false);
    console.log('Itinerary closed');
  };

  useEffect(() => {
    console.log('CHAT HISTORY: ', chatHistory);
  }, [chatHistory]);

  useEffect(() => {
    const idleInterval = window.setInterval(() => {
      if (Date.now() - lastActivityRef.current >= IDLE_TIMEOUT_MS) {
        console.log('Idle timeout reached, resetting conversation');
        stopSpeechRecognition();
        if (synthesizerRef.current) {
          synthesizerRef.current?.close();
          synthesizerRef.current = undefined;
        }
        audioQueueRef.current = [];
        isPlayingRef.current = false;
        clearFinalizeTimeout();
        clearCountdown();
        resetConversation();
        setSessionId(uuidv4());
        lastActivityRef.current = Date.now();
      }
    }, 30000);

    return () => window.clearInterval(idleInterval);
  }, [resetConversation, setSessionId, stopSpeechRecognition]);

  return (
    <div
      className={`relative h-screen w-screen overflow-hidden bg-yellow-400 ${!isItineraryOpen ? '' : ''}`}
    >
      {aiListening && questionTimeLeft > 0 && (
        <div className="absolute right-4 top-4 z-50 rounded-full bg-white/90 px-4 py-2 font-poppins text-sm font-semibold text-[#DD1D21] shadow">
          Time left {formatCountdown(questionTimeLeft)}
        </div>
      )}
      {settingsCardOpen && (
        <div className="absolute bottom-0 z-50 flex w-screen p-5">
          <SettingsCard />
        </div>
      )}

      {isItineraryOpen ? (
        width > 1024 ? (
          <div className="flex h-full w-full">
            {/* Left side - Spark Animation */}
            <div className="relative flex h-full w-1/2 items-center justify-center">
              {/* Spark Idle animation for when convo isn't active */}
              {aiIdle && !showHello && !showImSpark && (
                <AnimatePresence>
                  <motion.div
                    key={5}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ ease: 'easeOut', duration: 1 }}
                    exit={{ opacity: 0 }}
                    className="flex h-full w-full items-center justify-center"
                  >
                    <Lottie
                      loop
                      animationData={pectyIdle}
                      play
                      style={{ width: '80%', height: '80%' }}
                    />
                  </motion.div>
                </AnimatePresence>
              )}
              {/* Spark Listening animation for when we're waiting for a response */}
              {aiListening && (
                <AnimatePresence>
                  <motion.div
                    key={6}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ ease: 'easeOut', duration: 1 }}
                    exit={{ opacity: 0 }}
                    className="flex h-full w-full items-center justify-center"
                  >
                    <Lottie
                      loop
                      animationData={pectyListening}
                      play
                      style={{ width: '80%', height: '80%' }}
                    />
                  </motion.div>
                </AnimatePresence>
              )}
              {/* Spark Listening animation for when we're waiting for a response */}
              {aiThinking && (
                <AnimatePresence>
                  <motion.div
                    key={7}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ ease: 'easeOut', duration: 1 }}
                    exit={{ opacity: 0 }}
                    className="flex h-full w-full items-center justify-center"
                  >
                    <Lottie
                      loop
                      animationData={pectyResponding}
                      play
                      style={{ width: '80%', height: '80%' }}
                    />
                  </motion.div>
                </AnimatePresence>
              )}
              {/* Spark Happiness animation for when tts is talking */}
              {aiTalking && !aiThinking && (
                <AnimatePresence>
                  <motion.div
                    key={8}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ ease: 'easeOut', duration: 1 }}
                    exit={{ opacity: 0 }}
                    className="flex h-full w-full items-center justify-center"
                  >
                    <Lottie
                      loop
                      animationData={getEmotionLottie()}
                      play
                      style={{ width: '80%', height: '80%' }}
                    />
                  </motion.div>
                </AnimatePresence>
              )}
              {!showHello && !showImSpark && (
                <MicAndVisualizer
                  aiIdle={aiIdle}
                  aiListening={aiListening}
                  aiThinking={aiThinking}
                  aiTalking={aiTalking}
                  showMic={showMic}
                  isConvoReady={isConvoReady}
                  onClickListening={onClickListening}
                  onClickInterrupt={onClickInterrupt}
                  getIcon={getIcon}
                />
              )}
            </div>

            {/* Right side - Itinerary */}
            <div className="h-full w-1/2 overflow-y-auto p-5">
              <VoiceItinerary
                handleCloseItinerary={handleCloseItinerary}
                qrCodeURL={`${process.env.REACT_APP_URL}/spark-itinerary?sessionId=${sessionId}`}
                itinerary={itinerary}
              />
            </div>
          </div>
        ) : (
          <div className="h-full w-full p-5">
            <div className="ItineraryWrapper z-10 flex h-[85%] w-full">
              <VoiceItinerary
                handleCloseItinerary={handleCloseItinerary}
                qrCodeURL={`${process.env.REACT_APP_URL}/spark-itinerary?sessionId=${sessionId}`}
                itinerary={itinerary}
              />
            </div>
            {/* Spark Idle animation for when convo isn't active */}
            {aiIdle && !showHello && !showImSpark && (
              <AnimatePresence>
                <motion.div
                  key={5}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ ease: 'easeOut', duration: 1 }}
                  exit={{ opacity: 0 }}
                  className="absolute -bottom-[10rem] -left-[4rem] z-0"
                >
                  <Lottie
                    loop
                    animationData={pectyIdle}
                    play
                    style={{ width: 400, height: 400 }}
                  />
                </motion.div>
              </AnimatePresence>
            )}
            {/* Spark Listening animation for when we're waiting for a response */}
            {aiListening && (
              <AnimatePresence>
                <motion.div
                  key={6}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ ease: 'easeOut', duration: 1 }}
                  exit={{ opacity: 0 }}
                  className="absolute -bottom-[10rem] -left-[4rem] z-0"
                >
                  <Lottie
                    loop
                    animationData={pectyListening}
                    play
                    style={{ width: 400, height: 400 }}
                  />
                </motion.div>
              </AnimatePresence>
            )}
            {/* Spark Listening animation for when we're waiting for a response */}
            {aiThinking && (
              <AnimatePresence>
                <motion.div
                  key={7}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ ease: 'easeOut', duration: 1 }}
                  exit={{ opacity: 0 }}
                  className="absolute -bottom-[10rem] -left-[4rem] z-0"
                >
                  <Lottie
                    loop
                    animationData={pectyResponding}
                    play
                    style={{ width: 400, height: 400 }}
                  />
                </motion.div>
              </AnimatePresence>
            )}
            {/* Spark Happiness animation for when tts is talking */}
            {aiTalking && (
              <AnimatePresence>
                <motion.div
                  key={8}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ ease: 'easeOut', duration: 1 }}
                  exit={{ opacity: 0 }}
                  className="absolute -bottom-[10rem] -left-[4rem] z-0"
                >
                  <Lottie
                    loop
                    animationData={getEmotionLottie()}
                    play
                    style={{ width: 400, height: 400 }}
                  />
                </motion.div>
              </AnimatePresence>
            )}

            {isConvoReady && !showHello && !showImSpark && (
              <MicAndVisualizer
                aiIdle={aiIdle}
                aiListening={aiListening}
                aiThinking={aiThinking}
                aiTalking={aiTalking}
                showMic={showMic}
                isConvoReady={isConvoReady}
                onClickListening={onClickListening}
                onClickInterrupt={onClickInterrupt}
                getIcon={getIcon}
              />
            )}
          </div>
        )
      ) : (
        <div className="flex h-full w-full items-center justify-center text-lg ">
          {showHello && !convoStarted && (
            <AnimatePresence>
              <motion.div
                key={1}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ ease: 'easeOut', duration: 1 }}
                exit={{ opacity: 0 }}
                className=" absolute left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 transform"
              >
                <Lottie
                  loop
                  animationData={pectyJoy}
                  play
                  style={getAnimationSize()}
                />
              </motion.div>
              <motion.img
                className=" z-0"
                initial={{ opacity: 0, width: 400, height: 400 }}
                animate={{
                  opacity: 1,
                  width: 350,
                  height: 350,
                }}
                transition={{
                  repeat: Infinity,
                  duration: 4,
                  ease: 'easeOut',
                }}
                src={Dots}
                alt="Dots"
              />
            </AnimatePresence>
          )}
          {showImSpark && !convoStarted && (
            <AnimatePresence>
              <motion.div
                key={2}
                initial={{ opacity: 0.5 }}
                animate={{ opacity: 1 }}
                transition={{ ease: 'easeOut', duration: 4, delay: 0.5 }}
                exit={{ opacity: 0 }}
                className=" absolute left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 transform"
              >
                <Lottie
                  loop
                  animationData={pectyWave}
                  play
                  style={getAnimationSize()}
                />
              </motion.div>
              <motion.img
                className=" z-0"
                initial={{ opacity: 0, width: 400, height: 400 }}
                animate={{
                  opacity: 1,
                  width: 700,
                  height: 700,
                }}
                transition={{
                  duration: 3,
                  ease: 'easeOut',
                }}
                src={Dots}
                alt="Dots"
              />
            </AnimatePresence>
          )}
          {(showHello || showImSpark) && !convoStarted && (
            <AnimatePresence>
              <motion.div
                key={3}
                initial={{ opacity: 0, bottom: 0 }}
                animate={{ opacity: 1, bottom: 150 }}
                transition={{ ease: 'easeOut', duration: 3 }}
                exit={{ opacity: 0 }}
                className="Hello absolute flex flex-col text-center font-poppins text-6xl font-light leading-10 text-white"
              >
                {'Hello!'}
              </motion.div>
              <motion.div
                key={4}
                initial={{ opacity: 0, bottom: 50 }}
                animate={{ opacity: 1, bottom: 100 }}
                transition={{ ease: 'easeOut', duration: 1, delay: 3 }}
                exit={{ opacity: 0 }}
                className="Hello absolute flex flex-col text-center font-poppins text-3xl font-light leading-10 text-white"
              >
                {"I'm Shell Spark."}
              </motion.div>
            </AnimatePresence>
          )}
          {/* Spark Idle animation for when convo isn't active */}
          {aiIdle && !showHello && !showImSpark && (
            <AnimatePresence>
              <motion.div
                key={5}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ ease: 'easeOut', duration: 1 }}
                exit={{ opacity: 0 }}
                className=" absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform"
              >
                <Lottie
                  loop
                  animationData={pectyIdle}
                  play
                  style={getAnimationSize()}
                />
              </motion.div>
            </AnimatePresence>
          )}
          {/* Spark Listening animation for when we're waiting for a response */}
          {aiListening && (
            <AnimatePresence>
              <motion.div
                key={6}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ ease: 'easeOut', duration: 1 }}
                exit={{ opacity: 0 }}
                className=" absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform"
              >
                <Lottie
                  loop
                  animationData={pectyListening}
                  play
                  style={getAnimationSize()}
                />
              </motion.div>
            </AnimatePresence>
          )}
          {/* Spark Listening animation for when we're waiting for a response */}
          {aiThinking && (
            <AnimatePresence>
              <motion.div
                key={7}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ ease: 'easeOut', duration: 1 }}
                exit={{ opacity: 0 }}
                className=" absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform"
              >
                <Lottie
                  loop
                  animationData={pectyResponding}
                  play
                  style={getAnimationSize()}
                />
              </motion.div>
            </AnimatePresence>
          )}
          {/* Spark Happiness animation for when tts is talking */}
          {aiTalking && (
            <AnimatePresence>
              <motion.div
                key={8}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ ease: 'easeOut', duration: 1 }}
                exit={{ opacity: 0 }}
                className=" absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform"
              >
                <Lottie
                  loop
                  animationData={getEmotionLottie()}
                  play
                  style={getAnimationSize()}
                />
              </motion.div>
            </AnimatePresence>
          )}
          {/* Prompt Suggestions */}
          <>
            <AnimatePresence>
              {showYouCanSay && initialLoad && !showHello && !showImSpark && (
                <motion.div
                  className="absolute left-4 top-4 z-50 w-full max-h-[20vh] p-4 md:w-[36%] md:max-w-sm text-black"
                  key={9}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ ease: 'easeOut', duration: 3 }}
                  exit={{ opacity: 0 }}
                >
                  <div className="flex h-full max-h-[35vh] w-full flex-col gap-3 overflow-hidden rounded-xl bg-[radial-gradient(ellipse_61.00%_61.00%_at_50.00%_50.00%,_rgba(255,_255,_255,_0.16)_68%,_rgba(255,_255,_255,_0.40)_100%)] p-4">
                    <div className="Header shrink-0 flex flex-col gap-2 font-poppins text-base font-normal leading-normal tracking-wide opacity-70 text-black">
                      {' '}
                      You can say
                    </div>

                    <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto text-black">
                      {/* <div className="Header WhatCanYouDo flex flex-col gap-5 font-poppins text-lg font-normal leading-relaxed tracking-tight text-black">
                        "What can you do?"
                        <hr className="border-t border-black" />
                      </div> */}
                      {currentAgent === 'shell' ? (
                        <>
                          <div className="Header flex flex-col gap-5 font-poppins text-lg font-normal leading-relaxed tracking-tight text-black">
                            "How do I earn Scene+ points with Shell GO+?"
                            <hr className="border-t border-black" />
                          </div>
                          <div className="Header flex flex-col gap-5 font-poppins text-lg font-normal leading-relaxed tracking-tight text-black">
                            "How do I redeem Scene+ points, and what benefits do
                            I get?"
                            <hr className="border-t border-black" />
                          </div>
                          <div className="Header flex flex-col gap-2 font-poppins text-lg font-normal leading-relaxed tracking-tight text-black">
                            "How do Scotiabank and Tangerine integrate with
                            Shell GO+?"
                          </div>
                        </>
                      ) : currentAgent === 'mair' ||
                        currentAgent === 'mair-gpt' ? (
                        <>
                          <div className="Header flex flex-col gap-5 font-poppins text-lg font-normal leading-relaxed tracking-tight text-black">
                            "What is the 2025 Tech Vision?"
                            <hr className="border-t border-black" />
                          </div>
                          <div className="Header flex flex-col gap-2 font-poppins text-lg font-normal leading-relaxed tracking-tight text-black">
                            "What is Innovation?"
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="Header flex flex-col gap-5 font-poppins text-lg font-normal leading-relaxed tracking-tight text-black">
                            "What is the trend 'Meet my Agent'?"
                            <hr className="border-t border-black" />
                          </div>
                          <div className="Header flex flex-col gap-2 font-poppins text-lg font-normal leading-relaxed tracking-tight text-black">
                            "What is the Liquid Studio?"
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </>
          {/* Show mic and audio visualizer if the conversation is ready and screen size is less than tablet */}

          {isConvoReady && !showHello && !showImSpark && (
            <MicAndVisualizer
              aiIdle={aiIdle}
              aiListening={aiListening}
              aiThinking={aiThinking}
              aiTalking={aiTalking}
              showMic={showMic}
              isConvoReady={isConvoReady}
              onClickListening={onClickListening}
              onClickInterrupt={onClickInterrupt}
              getIcon={getIcon}
            />
          )}
        </div>
      )}

      {!isItineraryOpen && (
        <AnimatePresence>
          <div className="absolute bottom-3 right-3 flex flex-col items-end">
            <span className="mb-3 text-xs font-poppins text-black bg-white/80 rounded-lg px-3 py-2 shadow">
              Please switch to chat if you notice 
              <br /> the voice experience is struggling
            </span>
            <motion.button
              key={10}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="ChatContainer"
              onClick={() => voiceToChat()}
            >
              <img src={Chat} alt="chat" className="h-12 w-12" />
            </motion.button>
          </div>
        </AnimatePresence>
      )}
      {!isMobile && !isItineraryOpen && (
        <AnimatePresence>
          <motion.button
            key={11}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="ChatContainer absolute bottom-3 right-20"
            onClick={handleToggleSettingsCard}
          >
            <SettingsGear />
          </motion.button>
        </AnimatePresence>
      )}
    </div>
  );
};

export default VoiceConversation;
